import React, { useState, useCallback, useEffect, useRef } from "react";
import { getUserProfileByUsernameOrAddress } from "../../../api/profile/profile";
import { ButtonSpinner, errorMessageHandler } from "../../../utils/utils";
import { useSelector, useDispatch } from "react-redux";
import MnemonicsConfirmationModal from "../../CreateNFT/SingleNFT/MnemonicsConfirmationModal";
import { setCypherSecret } from "../../../actions/auth";
import ContinueWithPassword from "../../CreateNFT/SingleNFT/ContinueWithPassword";
import CryptoJS from 'crypto-js';
import { placeOrder, transferNft } from "../../../api/nft/nft";
import MissingMnemonics from "../../Layouts/MissingMnemonics/MissingMnemonics";
import './PlaceOrder.css';

const PlaceOrder = ({ isOpen, onClose, id, onSuccess }) => {

    // 📦 Form state
    const [price, setPrice] = useState('');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [passwordModal, setShowPasswordModal] = useState(false);
    const [mnemonicsConfirmationModal, setShowMnemonicsConfirmationModal] = useState(false);
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.Profile.profile);
    const cypherSecret = useSelector((state) => state.Auth.cypherSecret);

    // Quick price suggestions
    const quickPrices = [10, 50, 100, 500, 1000];



    // ✅ Validation function
    const validateForm = useCallback(() => {
        const newErrors = {};

        const priceValue = parseFloat(price);
        if (!price) {
            newErrors.price = "Price is required.";
        }
        else if (Number.isNaN(priceValue) || priceValue <= 0) {
            newErrors.price = "Price must be a positive number greater than zero.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [price]);




    // 🚀 Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();


        // Check if user has NOT set up their wallet (public address)
        if (!currentUser.public_address) {
            return document.getElementById('confirmationMissingMnemonicsBtn').click();
        }

        // Validate form
        if (!validateForm()) {
            return;
        }

        // Show appropriate modal based on whether user has saved mnemonics
        if (cypherSecret) {
            setShowPasswordModal(true)
        }
        else {
            setShowMnemonicsConfirmationModal(true)
        }
    };

    const handleTransferNft = (mnemonics, publicAddress, saveMyMnemonics, mnemonicsPwd) => {
        if (!mnemonics) {

            setErrors(prev => ({
                ...prev,
                submit: "Mnemonics are required."
            }));
            return;
        }

        if (!publicAddress) {
            setErrors(prev => ({
                ...prev,
                submit: "Your profile address does not match the provided mnemonics. Please use the mnemonics you provided during sign-up.",
            }));

            return;
        }

        if (publicAddress.toLowerCase() !== currentUser?.public_address?.toLowerCase()) {
            setErrors(prev => ({
                ...prev,
                submit: "Your profile address does not match the provided mnemonics. Please use the mnemonics you provided during sign-up.",
            }));
            return;
        }
        // if (parseFloat(balances.NUC) <= 0) {
        //   setErrors(prev => ({
        //     ...prev,
        //     submit: "Balance should be greater than 0. Currently, you have 0 balance."
        //   }));
        // }

        // ✅ All good — process NFT minting
        processNft(mnemonics, saveMyMnemonics, mnemonicsPwd);
    };

    let processNft = async (mnemonics, saveMyMnemonics, mnemonicsPwd) => {
        if (saveMyMnemonics) {
            const cypher = CryptoJS.AES.encrypt(mnemonics, mnemonicsPwd).toString();
            dispatch(setCypherSecret(cypher));
        }

        try {
            setIsLoading(true)
            setErrors({})
            let { data } = await placeOrder({ nftId: id, price: price, secret: mnemonics })
            onSuccess(price, data.data.txHash)
            handleClose()
            setIsLoading(false)
        } catch (error) {
            console.error("Transfer error:", error);
            setErrors(prev => ({
                ...prev,
                submit: errorMessageHandler(error)
            }));
        } finally {
            setIsLoading(false);
        }
    }


    // 🔒 Handle modal close
    const handleClose = (result = false) => {
        setPrice('');
        setErrors({});
        setIsLoading(false);
        onClose();
    };

    const handleQuickPrice = (value) => {
        setPrice(value.toString());
        setErrors({});
    };

    if (!isOpen) return null;

    return (
        <>
          
            {/* Modal Backdrop */}
            <div
                className="modal-backdrop fade show"
                onClick={handleClose}
                style={{ zIndex: 1040 }}
            ></div>

            {/* Modal */}
            <div
                className="modal fade show"
                id="placeOrderModal"
                tabIndex="-1"
                aria-labelledby="placeOrderModalLabel"
                aria-modal="true"
                role="dialog"
                style={{ display: 'block', zIndex: 1050 }}
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content place-order-modal-content">
                        {/* Modern Header */}
                        <div className="modal-header border-0 pb-0">
                            <div className="w-100">
                                <div className="d-flex align-items-center mb-2">
                                    <div className="place-order-icon-wrapper me-3">
                                        <i className="fas fa-tag"></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <h5 className="modal-title mb-1 fw-bold" id="placeOrderModalLabel">
                                            List NFT for Sale
                                        </h5>
                                        <p className="text-muted small mb-0">
                                            Set your price and list on the marketplace
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleClose}
                                aria-label="Close"
                                disabled={isLoading}
                            />
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body pt-3">
                                {/* Info Card */}
                                <div className="listing-info-card mb-4">
                                    <div className="d-flex align-items-start">
                                        <i className="fas fa-info-circle me-2 mt-1"></i>
                                        <div className="flex-grow-1">
                                            <small>
                                                Your NFT will be available for instant purchase at the price you set.
                                                You can update or remove the listing anytime.
                                            </small>
                                        </div>
                                    </div>
                                </div>

                                {/* 💰 Price Field */}
                                <div className="mb-4">
                                    <label className="form-label fw-semibold d-flex align-items-center mb-2">
                                        <i className="fas fa-dollar-sign me-2 text-muted"></i>
                                        Listing Price <span className="text-danger ms-1">*</span>
                                    </label>
                                    <div className="price-input-wrapper">
                                        <input
                                            type="number"
                                            name="price"
                                            className={`form-control shadow-none price-input ${errors.price ? 'is-invalid-custom' :
                                                price && parseFloat(price) > 0 ? 'is-valid-custom' : ''
                                                }`}
                                            placeholder="Enter price..."
                                            value={price}
                                            onChange={(e) => {
                                                setErrors({})
                                                setPrice(e.target.value)
                                            }}
                                            disabled={isLoading}
                                            min="0"
                                            step="0.01"
                                            autoFocus
                                        />
                                        <span className="price-currency-badge">NUC</span>
                                        {price && parseFloat(price) > 0 && !errors.price && (
                                            <div style={{ marginRight: 8 }} className="price-validation-icon ">
                                                <i className="fas fa-check-circle text-success"></i>
                                            </div>
                                        )}
                                    </div>

                                    {errors.price && (
                                        <div className="error-feedback-custom mt-2">
                                            <i className="fas fa-exclamation-circle me-1"></i>
                                            {errors.price}
                                        </div>
                                    )}
                                </div>

                                {/* Quick Price Buttons */}
                                <div className="mb-4">
                                    <label className="form-label small text-muted mb-2">
                                        <i className="fas fa-bolt me-1"></i>
                                        Quick Select
                                    </label>
                                    <div className="quick-price-buttons">
                                        {quickPrices.map((quickPrice) => (
                                            <button
                                                key={quickPrice}
                                                type="button"
                                                className={`quick-price-btn ${price === quickPrice.toString() ? 'active' : ''}`}
                                                onClick={() => handleQuickPrice(quickPrice)}
                                                disabled={isLoading}
                                            >
                                                {quickPrice} NUC
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Price Preview */}
                                {price && parseFloat(price) > 0 && !errors.price && (
                                    <div className="price-preview-card">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span className="text-muted small">Your Listing Price</span>
                                            <span className="badge bg-success-subtle text-success">
                                                <i className="fas fa-check me-1"></i>
                                                Ready to List
                                            </span>
                                        </div>
                                        <div className="price-preview-amount">
                                            <span className="price-value">{parseFloat(price).toFixed(2)}</span>
                                            <span className="price-currency">NUC</span>
                                        </div>
                                    </div>
                                )}

                                {/* ⚠️ Submit Error */}
                                {errors.submit && (
                                    <div className="submit-error-card mt-3">
                                        <div className="d-flex align-items-start">
                                            <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                                            <div>
                                                <strong className="d-block mb-1">Listing Failed</strong>
                                                <span>{errors.submit}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn btn-outline-dark px-4 py-2"
                                    onClick={handleClose}
                                    disabled={isLoading}
                                >
                                    <i className="fas fa-times me-2"></i>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn BtnBlack px-4 py-2"
                                    disabled={isLoading || !price || parseFloat(price) <= 0}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Listing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-tag me-2"></i>
                                            List for Sale
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <MnemonicsConfirmationModal
                visible={mnemonicsConfirmationModal}
                currentUser={currentUser}
                onClose={() => setShowMnemonicsConfirmationModal(false)}
                onConfirm={(data) => {
                    handleTransferNft(data.mnemonics, data.publicAddress, data.saveMyMnemonics, data.mnemonicsPwd)
                    setShowMnemonicsConfirmationModal(false);
                }}
            ></MnemonicsConfirmationModal>

            <ContinueWithPassword
                visible={passwordModal}
                currentUser={currentUser}
                cypherSecret={cypherSecret}
                onClose={() => setShowPasswordModal(false)}
                onConfirm={(data) => {
                    if (data.forgotPassword) {
                        setShowPasswordModal(false)
                        setShowMnemonicsConfirmationModal(true);
                    }
                    else {
                        handleTransferNft(data.mnemonics, data.publicAddress)
                        setShowPasswordModal(false)
                    }
                }}
            />


            <MissingMnemonics callback={(e) => handleSubmit()} />

        </>
    );
};


export default PlaceOrder;



